import { useMemo } from 'react';
import {
  useAutocompleteSearch,
  useStableOptions,
} from '@/hooks/useAutocompleteSearch';
import { useGetZonesQuery } from '@/pages/Business/pages/LogisticOperator/services/zones.service';
import { Option } from '@/components/inputs/AutocompleteInput';

interface UseZonesAutocompleteProps {
  search?: string;
  minLength?: number;
}

const useZonesAutocomplete = ({
  search,
  minLength = 3,
}: UseZonesAutocompleteProps = {}) => {
  const { debouncedSearch, shouldSearch, isDebouncePending } =
    useAutocompleteSearch(search, minLength);

  const {
    data: zonesData,
    isFetching: isZonesFetching,
    error: zonesError,
  } = useGetZonesQuery(
    {
      paginated: { page: 1, pageSize: 100 },
      search: shouldSearch ? debouncedSearch : undefined,
    },
    { skip: !shouldSearch }
  );

  const isZonesLoading = shouldSearch && isZonesFetching;

  const resolvedZoneOptions: Option[] = useMemo(() => {
    if (!zonesData?.items) return [];

    return zonesData.items.map((zone) => ({
      label: zone.name,
      data: zone.id,
    }));
  }, [zonesData?.items]);

  const zoneOptions = useStableOptions(resolvedZoneOptions, {
    isPending: isZonesFetching || isDebouncePending,
    enabled: shouldSearch || isDebouncePending,
  });

  return {
    zoneOptions,
    isZonesLoading,
    zonesError,
  };
};

export default useZonesAutocomplete;
